import { QueryErrorMessages } from '../enum/cube-error-message.enum';
import {
  CubeTimeDimensionGranularity,
  Weekdays,
} from '../enum/cube-schema.enum';
import { TeamAnalyticsActivityV2Segments } from '../enum/team-analytics-activity/segments.enum';
import { ITeamAnalyticsActivityV2Enum } from '../enum/team-analytics-activity/team-analytics-activity.enum';
import { ICubeRepository } from '../interface/cube-repository.interface';
import { Query } from '@cubejs-client/core';
import {
  mapLatestFilteredTeamAppMetricsItemV2,
  mapLatestTeamMetricsV2,
  mapLatestFilteredTeamSubscriptionMetricsItemV2,
  mapUserCountV2,
  mapTeamTotalMetricsV2,
  mapAllTimeAverageTeamMetricsV2,
  mapAllTimeWeekdayAverageTeamAppMetricsV2,
  mapTeamAllTimeSeriesAppMetricsV2,
  mapTeamAverageTimeSeriesAppMetricsV2,
  mapTeamTimeSeriesAppMetricsV2,
  mapUserTimeSeriesAppMetricsV2,
} from '../mappers/team-analytics-activity.mapper';
import { ITeamAnalyticsActivityRepositoryV2 } from '../interface/team-analytics-activity-repository-v2.interface';
import { AllTimeAverageTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/all-time-average-team-app-metrics-result.dto';
import { AllTimeWeekdayAverageTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/all-time-weekday-average-team-app-metrics-result.dto';
import { LatestTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/latest-team-app-metrics-result.dto';
import { QueryLatestTeamAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-latest-team-app-metrics-request.dto';
import { QueryAllTimeAverageTeamAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-all-time-average-team-app-metrics-request.dto';
import { QueryAllTimeWeekdayAverageTeamAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-all-time-weekday-average-team-app-metrics-request.dto';
import { QueryAllTimeMaxAppMetricRequestParamsDto } from '../dto/team-app-metrics/request/query-all-time-max-app-metric-request.dto';
import { CubeConfigDto } from '../dto/common/request/cube-config.dto';
import { FilteredTeamAppMetricsV2Result } from '../dto/team-app-metrics/cube/mapped/filtered-team-app-metrics-result.dto';
import { FilteredTeamSubscriptionMetricsResult } from '../dto/team-app-metrics/cube/mapped/filtered-team-subscriptions-result.dto';
import { UserCountResult } from '../dto/team-app-metrics/cube/mapped/user-count-result.dto';
import { TeamTotalMetricsResultV2 } from '../dto/team-app-metrics/cube/mapped/team-total-metrics-result.dto';
import { QueryTeamTotalMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-team-total-metrics-request.dto';
import { QueryUserCountRequestParamsDto } from '../dto/team-app-metrics/request/query-user-count-request.dto';
import { QueryLatestFilteredTeamAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-latest-filtered-team-app-metrics-request.dto';
import { QueryLatestFilteredTeamSubscriptionMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-latest-filtered-team-subscription-metrics-request.dto';
import { QueryTeamUserTimeSeriesAppMetricsRequestParamsDto } from '../dto/top-apps/request/query-team-user-time-series-app-metrics-request.dto';
import { QueryTeamAverageTimeSeriesAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-team-average-time-series-app-metrics-request.dto';
import { QueryTeamTimeSeriesAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-team-time-series-app-metrics-request.dto';
import { QueryTeamTimeSeriesAllTimeAverageAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-team-time-series-all-time-average-app-metrics-request.dto';
import { TeamTimeSeriesAllTimeAverageResult } from '../dto/team-app-metrics/cube/mapped/team-time-series-all-time-average-result.dto';
import {
  TeamTimeSeriesResult,
  TeamUserTimeSeriesResult,
} from '../dto/team-app-metrics/cube/mapped/team-time-series-result.dto';
import dayjs from 'dayjs';
import dayjsTimezonePlugin from 'dayjs/plugin/timezone';
import dayjsUtcPlugin from 'dayjs/plugin/utc';
dayjs.extend(dayjsUtcPlugin);
dayjs.extend(dayjsTimezonePlugin);

export class TeamAnalyticsActivityRepositoryV2
  implements ITeamAnalyticsActivityRepositoryV2
{
  cubeRepository: ICubeRepository;
  constructor(deps: { repository: ICubeRepository }) {
    this.cubeRepository = deps.repository;
  }

  async queryLatestTeamAppMetrics(
    params: QueryLatestTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<LatestTeamAppMetricsResult> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
          ITeamAnalyticsActivityV2Enum.CallCount,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.DayCount,
          ITeamAnalyticsActivityV2Enum.UserCount,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? [params.startDate, params.endDate]
                : [params.endDate, params.endDate],
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'desc']],
      };

      if (params?.userIds?.length)
        query.filters.push({
          member: ITeamAnalyticsActivityV2Enum.UserId,
          operator: 'equals',
          values: params.userIds,
        });

      const latestTeamMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapLatestTeamMetricsV2(latestTeamMetrics);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryLatestFilteredTeamAppMetrics(
    params: QueryLatestFilteredTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<Omit<FilteredTeamAppMetricsV2Result, 'userCount'>[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
          ITeamAnalyticsActivityV2Enum.CallCount,
        ],
        dimensions: [ITeamAnalyticsActivityV2Enum.AppId],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'asc']],
      };

      if (params?.userIds?.length)
        query.filters.push({
          member: ITeamAnalyticsActivityV2Enum.UserId,
          operator: 'equals',
          values: params.userIds,
        });

      const latestTeamMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      return latestTeamMetrics.map((app) =>
        mapLatestFilteredTeamAppMetricsItemV2(app)
      );
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryLatestFilteredTeamSubscriptionMetrics(
    params: QueryLatestFilteredTeamSubscriptionMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<FilteredTeamSubscriptionMetricsResult[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
          ITeamAnalyticsActivityV2Enum.CallCount,
        ],
        dimensions: [
          ITeamAnalyticsActivityV2Enum.AppId,
          ITeamAnalyticsActivityV2Enum.SubscriptionId,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'asc']],
      };

      if (params?.userIds?.length)
        query.filters.push({
          member: ITeamAnalyticsActivityV2Enum.UserId,
          operator: 'equals',
          values: params.userIds,
        });

      const latestTeamMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      return latestTeamMetrics.map((app) =>
        mapLatestFilteredTeamSubscriptionMetricsItemV2(app)
      );
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryUserCount(
    params: QueryUserCountRequestParamsDto,
    config: CubeConfigDto
  ): Promise<UserCountResult[]> {
    try {
      const query: Query = {
        measures: [ITeamAnalyticsActivityV2Enum.UserCount],
        dimensions: params.includeSubscriptions
          ? [
              ITeamAnalyticsActivityV2Enum.AppId,
              ITeamAnalyticsActivityV2Enum.SubscriptionId,
            ]
          : [ITeamAnalyticsActivityV2Enum.AppId],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        filters: params.userIds?.length
          ? [
              {
                member: ITeamAnalyticsActivityV2Enum.TeamId,
                operator: 'equals',
                values: [params.teamId],
              },
              {
                member: ITeamAnalyticsActivityV2Enum.AppId,
                operator: 'equals',
                values: params.appIds,
              },
              {
                member: ITeamAnalyticsActivityV2Enum.UserId,
                operator: 'equals',
                values: params.userIds,
              },
            ]
          : [
              {
                member: ITeamAnalyticsActivityV2Enum.TeamId,
                operator: 'equals',
                values: [params.teamId],
              },
              {
                member: ITeamAnalyticsActivityV2Enum.AppId,
                operator: 'equals',
                values: params.appIds,
              },
            ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'asc']],
      };

      const userCounts = await this.cubeRepository.query(config.token, query);
      return userCounts.map((app) => mapUserCountV2(app));
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryTeamTotalMetrics(
    params: QueryTeamTotalMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTotalMetricsResultV2> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.UserCount,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
          ITeamAnalyticsActivityV2Enum.CallCount,
          ITeamAnalyticsActivityV2Enum.DayCount,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'asc']],
      };

      if (params?.userIds?.length) {
        query.filters.push({
          member: ITeamAnalyticsActivityV2Enum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      const [totalMetrics] = await this.cubeRepository.query(
        config.token,
        query
      );
      return mapTeamTotalMetricsV2(totalMetrics);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryAllTimeAverageTeamAppMetrics(
    params: QueryAllTimeAverageTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AllTimeAverageTeamAppMetricsResult> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.DayCount,
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.UserCount,
          ITeamAnalyticsActivityV2Enum.WeekCount,
          ITeamAnalyticsActivityV2Enum.CallCount,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
          },
        ],
        order: {},
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
      };

      if (params?.userIds?.length) {
        query.filters.push({
          member: ITeamAnalyticsActivityV2Enum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      const allTimeAverageTeamMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      const result =
        params.frequency === CubeTimeDimensionGranularity.Week
          ? mapAllTimeAverageTeamMetricsV2(
              allTimeAverageTeamMetrics,
              CubeTimeDimensionGranularity.Week
            )
          : mapAllTimeAverageTeamMetricsV2(allTimeAverageTeamMetrics);

      return result;
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryAllTimeAverageTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryAllTimeWeekdayAverageTeamAppMetrics(
    params: QueryAllTimeWeekdayAverageTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AllTimeWeekdayAverageTeamAppMetricsResult> {
    try {
      if (params.frequency === CubeTimeDimensionGranularity.Week) {
        throw new Error(
          `Cannot query for weekday average duration with frequency: ${params.frequency}`
        );
      }

      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.DayCount,
          ITeamAnalyticsActivityV2Enum.UserCount,
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.CallCount,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'desc']],
        segments: [],
      };

      if (params.frequency === CubeTimeDimensionGranularity.Day) {
        const dayIndex = new Date(
          new Date(params.endDate).getTime() +
            Math.abs(dayjs.utc().tz(params.timezone).utcOffset() * 60 * 1000)
        ).getDay();
        const dayOfWeek = Object.values(Weekdays)[dayIndex];
        query.segments.push(TeamAnalyticsActivityV2Segments[dayOfWeek]);
      } else {
        query.segments.push(TeamAnalyticsActivityV2Segments[params.frequency]);
      }

      if (params?.userIds?.length) {
        query.filters.push({
          member: ITeamAnalyticsActivityV2Enum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      const weekdayAverages = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapAllTimeWeekdayAverageTeamAppMetricsV2(weekdayAverages);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryAllTimeWeekdayAverageAppDurationError}: ${e}`
      );
    }
  }

  async queryAllTimeMaxAppMetric(
    params: QueryAllTimeMaxAppMetricRequestParamsDto,
    config: CubeConfigDto,
    metric: string
  ): Promise<number> {
    try {
      const query: Query = {
        measures: [metric],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[metric, 'desc']],
        limit: 1,
        dimensions: [ITeamAnalyticsActivityV2Enum.AppId],
      };

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivityV2Segments[params.frequency]];
      }

      if (params?.userIds?.length) {
        query.filters.push({
          member: ITeamAnalyticsActivityV2Enum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      const maxDuration = await this.cubeRepository.query(config.token, query);

      if (!maxDuration.length) return null;

      return Number(maxDuration[0][metric]);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryAllTimeMaxAppMetricError}: ${e}`
      );
    }
  }

  async queryTeamUserTimeSeriesAppMetrics(
    params: QueryTeamUserTimeSeriesAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamUserTimeSeriesResult[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
          ITeamAnalyticsActivityV2Enum.CallCount,
          ITeamAnalyticsActivityV2Enum.DayCount,
        ],
        dimensions: [ITeamAnalyticsActivityV2Enum.UserId],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'desc']],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
      };

      if (params.userIds) {
        query.filters.push({
          member: ITeamAnalyticsActivityV2Enum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivityV2Segments[params.frequency]];
      }

      const userTimeSeriesData = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapUserTimeSeriesAppMetricsV2(userTimeSeriesData);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryTeamUserTimeSeriesAppMetricsError}: ${e}`
      );
    }
  }

  async queryTeamAverageTimeSeriesAppMetrics(
    params: QueryTeamAverageTimeSeriesAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTimeSeriesResult[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.DayCount,
          ITeamAnalyticsActivityV2Enum.UserCount,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.CallCount,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'desc']],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
      };

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivityV2Segments[params.frequency]];
      }

      const teamTimeSeriesData = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapTeamAverageTimeSeriesAppMetricsV2(teamTimeSeriesData);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryTeamAverageTimeSeriesAppMetricsError}: ${e}`
      );
    }
  }

  async queryTeamTimeSeriesAppMetrics(
    params: QueryTeamTimeSeriesAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTimeSeriesResult[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
          ITeamAnalyticsActivityV2Enum.CallCount,
          ITeamAnalyticsActivityV2Enum.DayCount,
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'desc']],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
      };

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivityV2Segments[params.frequency]];
      }

      const teamTimeSeriesData = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapTeamTimeSeriesAppMetricsV2(teamTimeSeriesData);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryTeamTimeSeriesAppMetricsError}: ${e}`
      );
    }
  }

  async queryTeamTimeSeriesAllTimeAverageAppMetrics(
    params: QueryTeamTimeSeriesAllTimeAverageAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTimeSeriesAllTimeAverageResult> {
    try {
      const query: Query = {
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityV2Enum.LocalStartTime,
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityV2Enum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityV2Enum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityV2Enum.LocalStartTime, 'desc']],
        measures: [
          ITeamAnalyticsActivityV2Enum.TotalDuration,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration,
          ITeamAnalyticsActivityV2Enum.Count,
          ITeamAnalyticsActivityV2Enum.CallCount,
          ITeamAnalyticsActivityV2Enum.DayCount,
          ITeamAnalyticsActivityV2Enum.WeekCount,
        ],
      };

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivityV2Segments[params.frequency]];
      }

      const teamAllTimeSeriesMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      const result =
        params.frequency === CubeTimeDimensionGranularity.Week
          ? mapTeamAllTimeSeriesAppMetricsV2(
              teamAllTimeSeriesMetrics,
              CubeTimeDimensionGranularity.Week
            )
          : mapTeamAllTimeSeriesAppMetricsV2(teamAllTimeSeriesMetrics);

      return result;
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryTeamAllTimeSeriesAppMetricsError}: ${e}`
      );
    }
  }
}
